// Mirrors the API's src/constants/frameworks.utils.js so any code that
// normalises a framework on the client behaves identically to the server.
import {
  ESG_FRAMEWORK_SET,
  FRAMEWORK_ALIASES,
  FRAMEWORK_CANONICAL_BY_LOWER,
  JJ_GROUP_FRAMEWORK_CODE,
  SYNTHETIC_FRAMEWORK_SET,
} from "@/lib/constants/frameworks.constants";

const ALIAS_BY_LOWER = new Map(
  Object.entries(FRAMEWORK_ALIASES).map(([k, v]) => [k.toLowerCase(), v]),
);

export const normalizeFramework = (value, { allowSynthetic = false } = {}) => {
  if (typeof value !== "string") return null;
  const trimmed = value.trim();
  if (!trimmed) return null;
  if (ESG_FRAMEWORK_SET.has(trimmed)) return trimmed;
  if (allowSynthetic && SYNTHETIC_FRAMEWORK_SET.has(trimmed)) return trimmed;
  const lower = trimmed.toLowerCase();
  const canonical = FRAMEWORK_CANONICAL_BY_LOWER.get(lower);
  if (canonical) return canonical;
  if (allowSynthetic) {
    const upper = trimmed.toUpperCase();
    if (SYNTHETIC_FRAMEWORK_SET.has(upper)) return upper;
  }
  return null;
};

const PREFERRED_FRAMEWORK_CODE = JJ_GROUP_FRAMEWORK_CODE;

const normalizeFrameworkCode = (value) => {
  const trimmed = String(value || "").trim();
  if (!trimmed) return "";
  return normalizeFramework(trimmed) || trimmed;
};

const extractFrameworkCode = (framework) => {
  if (typeof framework === "string") return framework;
  return (
    framework?.code ??
    framework?.framework ??
    framework?.value ??
    framework?.id ??
    framework?._id ??
    ""
  );
};

export const getPreferredFrameworkCode = (
  frameworks = [],
  preferredCode = PREFERRED_FRAMEWORK_CODE,
) => {
  const normalizedPreferredCode = normalizeFrameworkCode(preferredCode);
  if (!normalizedPreferredCode) return "";

  return (
    (Array.isArray(frameworks) ? frameworks : [])
      .map((framework) =>
        normalizeFrameworkCode(extractFrameworkCode(framework)),
      )
      .find((code) => code === normalizedPreferredCode) || ""
  );
};

export const buildAssignedFrameworks = ({
  disclosures = [],
  frameworks = [],
} = {}) => {
  const assignedCodes = new Set(
    (Array.isArray(disclosures) ? disclosures : [])
      .map((disclosure) => normalizeFrameworkCode(disclosure?.framework))
      .filter(Boolean),
  );

  if (!assignedCodes.size) return [];

  const seenCodes = new Set();
  const scopedFrameworks = (Array.isArray(frameworks) ? frameworks : [])
    .map((framework) => {
      const code = normalizeFrameworkCode(framework?.code ?? framework);
      if (!code || !assignedCodes.has(code)) return null;
      seenCodes.add(code);
      return typeof framework === "string"
        ? { code, name: code }
        : {
            ...framework,
            code,
            name: framework?.name || framework?.label || code,
          };
    })
    .filter(Boolean);

  for (const code of assignedCodes) {
    if (!seenCodes.has(code)) {
      scopedFrameworks.push({ code, name: code });
    }
  }

  return scopedFrameworks;
};

export const expandFrameworkRequest = (value) => {
  if (typeof value !== "string") return [];
  const trimmed = value.trim();
  if (!trimmed) return [];
  const aliasHit = ALIAS_BY_LOWER.get(trimmed.toLowerCase());
  if (aliasHit) return [...aliasHit];
  const canonical = normalizeFramework(trimmed);
  return canonical ? [canonical] : [];
};

// Boundary helper for outgoing API requests. Accepts a single value, a
// single-element array, or any array of mixed-case / aliased / IFRS-umbrella
// strings and emits a deduped array of canonical codes. Anything that
// cannot be resolved is dropped silently — Joi on the server side is the
// authoritative gate.
export const toApiFrameworks = (input) => {
  const list = Array.isArray(input) ? input : input ? [input] : [];
  const out = new Set();
  for (const v of list) {
    for (const c of expandFrameworkRequest(v)) out.add(c);
  }
  return [...out];
};
